/**
 * Tenant LLM-tier config API (workspace "LLM" tab).
 *
 * A tenant OWNER/ADMIN rebinds Reason/Think/Fast to concrete models; networks/rooms/agents stay
 * tier-agnostic. Mirrors budgetApi's auth/base-url shape.
 *
 *   GET  /tenants/{tid}/llm-config          → { tiers, defaults, options }
 *   PUT  /tenants/{tid}/llm-config          → save { tiers } (OWNER/ADMIN)
 *   GET  /tenants/{tid}/llm-config/models   → { invokable: [modelId, …] }  (availability probe)
 */
import { getRestApiBearerToken } from '@/services/apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

function _enc(s) {
  return encodeURIComponent(String(s || ''));
}

async function _req(path, init) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...((init && init.headers) || {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || body.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = body;
    throw err;
  }
  return body;
}

/** GET the tenant's tier→model config + deploy defaults + candidate options per tier. */
export function getTenantLlmConfig(tenantId) {
  return _req(`/tenants/${_enc(tenantId)}/llm-config`, { method: 'GET' });
}

/** PUT the tenant's LLM config. Accepts a partial payload — ``{tiers}`` maps {reason,think,fast}→
 *  modelId; ``{params}`` maps {role:{maxTokens?,temperature?}} model-parameter overrides;
 *  ``{globals}`` holds non-role settings (e.g. rulesInjectionBudgetChars). All live on the same row
 *  and the backend MERGE-PRESERVES, so any single-key save is safe. Back-compat: a bare object with
 *  tier keys is treated as ``{tiers}``. OWNER/ADMIN only. */
export function putTenantLlmConfig(tenantId, payload) {
  const p = payload || {};
  // If the caller passed a raw {reason,think,fast} map (legacy signature), wrap it as {tiers}.
  const body = ('tiers' in p || 'params' in p || 'globals' in p) ? p : { tiers: p };
  return _req(`/tenants/${_enc(tenantId)}/llm-config`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** GET the availability probe — which candidate models this account can actually invoke. */
export function getTenantLlmInvokableModels(tenantId) {
  return _req(`/tenants/${_enc(tenantId)}/llm-config/models`, { method: 'GET' });
}
